import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useCrmObjectType, useFiltersStateStore } from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import { getInitialFiltersStateForUIMode, removePropertyFilters, updateFilterState } from 'framework-listing-lib/utils/filters';
import useExpandCollapseRow from 'framework-listing-lib/hooks/useExpandCollapseRow';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import { useHasViews } from '../internal/ViewTabs/hooks/useViewProps';
import useUIMode from 'framework-listing-lib/internal/hooks/useUIMode';
import useCurrentView, { useCurrentViewFilters } from 'framework-listing-lib/hooks/useCurrentView';
import { useFilterGroups } from './useListingLibFilters';
import useQuickFilters from './useQuickFilters';
/**
 * This hook reads the global filter from CrmObjectTypeListingClientProvider and provides two
 * main methods to manipulate filters in the codebase:
 *  - onUpdateFilters: to update/override the filter state.
 *  - onDeleteObject: to delete & ignore objectIds from upcoming requests to crm-search.
 *
 * It also propagates current applied filters as well as list of deleted objects.
 *
 * Components & hooks that need to read/write filter state should pull the filter information from this hook (useCrmObjectTypeListingFilters).
 */
function useCrmObjectTypeListingFilters() {
  "use memo";

  const $ = _c(35);
  const uiModeProps = useUIMode();
  const {
    hiddenFilter,
    isFilterFromCustomView,
    initialFilter
  } = useCrmObjectType();
  const onSetFilters = useFiltersStateStore(_temp);
  const onSetDeletedObjects = useFiltersStateStore(_temp2);
  const filtersState = useFiltersStateStore(_temp3);
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const deletedObjects = useFiltersStateStore(_temp4);
  const hasViews = useHasViews();
  const currentView = useCurrentView();
  const currentViewFilters = useCurrentViewFilters();
  const {
    collapseAll
  } = useExpandCollapseRow();
  let t0;
  if ($[0] !== filterGroups || $[1] !== quickFilters.length) {
    t0 = filterGroups.some(_temp5) || quickFilters.length > 0;
    $[0] = filterGroups;
    $[1] = quickFilters.length;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasAppliedFilters = t0;
  let t1;
  if ($[3] !== collapseAll || $[4] !== onSetFilters) {
    t1 = propertyName => {
      onSetFilters(prevFilters => Object.assign({}, prevFilters, {
        filterGroups: removePropertyFilters(prevFilters.filterGroups, propertyName)
      }));
      collapseAll();
    };
    $[3] = collapseAll;
    $[4] = onSetFilters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onRemovePropertyFilter = t1;
  const {
    onUpdateFilters
  } = useUpdateFilters();
  let t2;
  if ($[6] !== currentView || $[7] !== currentViewFilters.filterGroups || $[8] !== currentViewFilters.quickFilters || $[9] !== hasViews || $[10] !== initialFilter || $[11] !== onSetFilters || $[12] !== onUpdateFilters || $[13] !== uiModeProps.currentUIMode) {
    t2 = t3 => {
      const {
        newUIMode
      } = t3 === undefined ? {} : t3;
      const isGoingToFoldersMode = Boolean(newUIMode && newUIMode !== uiModeProps.currentUIMode && newUIMode === UI_MODE.FOLDERS);
      if (isGoingToFoldersMode) {
        onSetFilters(getInitialFiltersStateForUIMode({
          initialFilter,
          uiMode: UI_MODE.FOLDERS
        }));
        return;
      }
      const initialFilterGroups = initialFilter && initialFilter.filterGroups ? initialFilter.filterGroups : [];
      const initialQuickFilters = initialFilter && initialFilter.quickFilters ? initialFilter.quickFilters : [];
      if (hasViews && currentView) {
        onUpdateFilters({
          filterGroups: currentViewFilters.filterGroups,
          quickFilters: currentViewFilters.quickFilters,
          query: ""
        });
      } else {
        onUpdateFilters({
          filterGroups: initialFilterGroups,
          quickFilters: initialQuickFilters,
          query: ""
        });
      }
    };
    $[6] = currentView;
    $[7] = currentViewFilters.filterGroups;
    $[8] = currentViewFilters.quickFilters;
    $[9] = hasViews;
    $[10] = initialFilter;
    $[11] = onSetFilters;
    $[12] = onUpdateFilters;
    $[13] = uiModeProps.currentUIMode;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const onClearFilters = t2;
  let t3;
  if ($[15] !== onSetDeletedObjects) {
    t3 = () => {
      onSetDeletedObjects([]);
    };
    $[15] = onSetDeletedObjects;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const onClearDeletedObjects = t3;
  let t4;
  if ($[17] !== onSetDeletedObjects) {
    t4 = objectIdOrIds => {
      if (Array.isArray(objectIdOrIds)) {
        onSetDeletedObjects(prevDeletedObjects => [...prevDeletedObjects, ...objectIdOrIds]);
      } else {
        onSetDeletedObjects(prevDeletedObjects_0 => [...prevDeletedObjects_0, objectIdOrIds]);
      }
    };
    $[17] = onSetDeletedObjects;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const onDeleteObject = t4;
  let t5;
  if ($[19] !== filterGroups || $[20] !== filtersState || $[21] !== quickFilters) {
    t5 = Object.assign({}, filtersState, {
      filterGroups,
      quickFilters
    });
    $[19] = filterGroups;
    $[20] = filtersState;
    $[21] = quickFilters;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== deletedObjects || $[24] !== hasAppliedFilters || $[25] !== hiddenFilter || $[26] !== initialFilter || $[27] !== isFilterFromCustomView || $[28] !== onClearDeletedObjects || $[29] !== onClearFilters || $[30] !== onDeleteObject || $[31] !== onRemovePropertyFilter || $[32] !== onUpdateFilters || $[33] !== t5) {
    t6 = {
      deletedObjects,
      filters: t5,
      hasAppliedFilters,
      hiddenFilter,
      initialFilter,
      isFilterFromCustomView,
      onClearDeletedObjects,
      onClearFilters,
      onDeleteObject,
      onRemovePropertyFilter,
      onResetFilters: onClearFilters,
      onUpdateFilters
    };
    $[23] = deletedObjects;
    $[24] = hasAppliedFilters;
    $[25] = hiddenFilter;
    $[26] = initialFilter;
    $[27] = isFilterFromCustomView;
    $[28] = onClearDeletedObjects;
    $[29] = onClearFilters;
    $[30] = onDeleteObject;
    $[31] = onRemovePropertyFilter;
    $[32] = onUpdateFilters;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  const value = t6;
  return value;
}
function _temp5(filterGroup) {
  return filterGroup.filters.length > 0;
}
function _temp4(state_2) {
  return state_2.deletedObjects;
}
function _temp3(state_1) {
  return state_1.filtersState;
}
function _temp2(state_0) {
  return state_0.setDeletedObjects;
}
function _temp(state) {
  return state.setFilters;
}
export function useUpdateFilters() {
  const onSetFilters = useFiltersStateStore(state => state.setFilters);
  const {
    collapseAll
  } = useExpandCollapseRow();
  const onUpdateFilters = useCallback((updatedFilter, internalOptions) => {
    onSetFilters(prevFilters => updateFilterState(prevFilters, updatedFilter), internalOptions);
    collapseAll();
  }, [collapseAll, onSetFilters]);
  return {
    onUpdateFilters
  };
}
export default useCrmObjectTypeListingFilters;